import { Calendar, Mail, Sparkles, Target } from "lucide-react";
import { motion, useInView, useReducedMotion } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { createStaggerContainer, createStaggerItem, REVEAL_AMOUNT } from "@/src/common/components/animated-section";
import { MOCK_AI_ANSWERS } from "@/src/common/components/app-mock/mock-community";
import { WebAiInsightsScreen } from "@/src/common/components/app-mock/web-screens/web-ai-screen";
import { WebAppShell } from "@/src/common/components/app-mock/web-shell";
import { BrowserFrame } from "@/src/common/components/browser-frame";
import { EyebrowBadge } from "@/src/common/components/eyebrow-badge";
import { useCommunityVariant } from "@/src/common/components/product-demo/community-variant-context";
import { Reveal } from "@/src/common/components/reveal";
import { SectionHeading } from "@/src/common/components/section-heading";
import { toEntranceVariant, useEntrance } from "@/src/common/hooks/use-entrance";

const FEATURES: ReadonlyArray<{ idx: "1" | "2" | "3"; Icon: typeof Mail }> = [
	{ idx: "1", Icon: Mail },
	{ idx: "2", Icon: Target },
	{ idx: "3", Icon: Calendar },
];

const ANSWER_ID = "growth";
const TYPING_MS = 28;
const ANSWER_DELAY_MS = 350;

const FULL_QUESTION = MOCK_AI_ANSWERS.find((answer) => answer.id === ANSWER_ID)?.question ?? "";

function useTypedQuestion(active: boolean) {
	const [typed, setTyped] = useState(FULL_QUESTION);
	const [showAnswer, setShowAnswer] = useState(true);

	useEffect(() => {
		if (!active) return;
		setTyped("");
		setShowAnswer(false);
		let index = 0;
		let answerTimeout = 0;
		const interval = window.setInterval(() => {
			index += 1;
			setTyped(FULL_QUESTION.slice(0, index));
			if (index >= FULL_QUESTION.length) {
				window.clearInterval(interval);
				answerTimeout = window.setTimeout(() => setShowAnswer(true), ANSWER_DELAY_MS);
			}
		}, TYPING_MS);
		return () => {
			window.clearInterval(interval);
			window.clearTimeout(answerTimeout);
		};
	}, [active]);

	return { typed, showAnswer };
}

function AiInsightsVisual({ url }: { url: string }) {
	const { t } = useTranslation("home");
	const { palette } = useCommunityVariant();
	const reduced = useReducedMotion() === true;
	const ref = useRef<HTMLDivElement>(null);
	const inView = useInView(ref, { once: true, amount: 0.5 });
	const { typed, showAnswer } = useTypedQuestion(inView && !reduced);

	return (
		<div ref={ref}>
			<BrowserFrame url={url} tabTitle="AI Insights" faviconColor={palette.accent} label={t("ai_insights.frame")}>
				<div className="h-[460px] overflow-hidden">
					<WebAppShell activeNav="admin-ai" layout="compact" fullBleed scroll={false}>
						<WebAiInsightsScreen
							initialAnswerId={ANSWER_ID}
							promptChips={false}
							question={typed}
							showAnswer={showAnswer}
							answerClassName="demo-panel-enter"
						/>
					</WebAppShell>
				</div>
			</BrowserFrame>
		</div>
	);
}

export function AiInsightsTeaser() {
	const { t } = useTranslation("home");
	const reduced = useReducedMotion() === true;
	const listRef = useRef<HTMLUListElement>(null);
	const entrance = useEntrance(listRef, { amount: REVEAL_AMOUNT, disabled: reduced });
	const container = createStaggerContainer(reduced, 0.1);
	const item = createStaggerItem(reduced);

	return (
		<section className="bg-mesh py-24 md:py-32">
			<div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
				<Reveal className="order-2 lg:order-1">
					<AiInsightsVisual url={t("ai_insights.url")} />
				</Reveal>

				<div className="order-1 flex flex-col gap-6 lg:order-2">
					<SectionHeading
						align="left"
						eyebrow={
							<EyebrowBadge tone="ink" icon={<Sparkles className="h-3 w-3" />}>
								{t("ai_insights.eyebrow")}
							</EyebrowBadge>
						}
						title={t("ai_insights.title")}
						titleAccent={t("ai_insights.title_accent")}
						description={t("ai_insights.subtitle")}
					/>

					<motion.ul ref={listRef} className="mt-2 space-y-4" initial={false} animate={toEntranceVariant(entrance)} variants={container}>
						{FEATURES.map(({ idx, Icon }) => (
							<motion.li
								key={idx}
								variants={item}
								className="flex items-start gap-3 rounded-2xl border border-default-200 bg-white p-4 shadow-card hover-lift"
							>
								<span className="inline-flex size-9 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
									<Icon className="h-4 w-4" />
								</span>
								<div>
									<p className="text-sm font-bold text-foreground">{t(`ai_insights.feature_${idx}.title`)}</p>
									<p className="mt-0.5 text-sm text-default-600">{t(`ai_insights.feature_${idx}.body`)}</p>
								</div>
							</motion.li>
						))}
					</motion.ul>
				</div>
			</div>
		</section>
	);
}
